import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRosteringAccess } from '../hooks/useRosteringAccess';
import type { Employee } from '../../admin/types/Employee';
import { Spinner } from '../../../shared/components';
import { useToast } from '../../notifications';
import { listRosterEmployees, listRosterRules } from '../services/rosteringService';
import type { RosterScheduleRule, RosterViewMode, RosterWeekDay, WeekScheduleTab } from '../types/Rostering';
import {
  buildRosterCellMap,
  getMonthDays,
  getWeekDays,
  startOfMonth,
  startOfWeekMonday,
  toDateKey,
} from '../utils/rosterUtils';
import {
  buildEmployeeScheduleUrl,
  buildRosterListUrl,
  parseMonthParam,
  parseRosterViewMode,
} from '../utils/rosterViewUtils';
import { parseWeekStartParam } from '../utils/weekScheduleUtils';
import { ViewModeToggle } from './ViewModeToggle';
import { WeekRangePicker } from './WeekRangePicker';
import headerStyles from './RosterHeader.module.css';
import styles from './RosterPage.module.css';

type OverviewKind = 'schedule' | 'onCall';

type OverviewConfig = {
  kind: OverviewKind;
  titleKey: 'scheduleOverview' | 'onCallOverview';
  emptyKey: 'off' | 'notOnCall';
  tab: WeekScheduleTab;
};

const OVERVIEWS: OverviewConfig[] = [
  { kind: 'schedule', titleKey: 'scheduleOverview', emptyKey: 'off', tab: 'schedule' },
  { kind: 'onCall', titleKey: 'onCallOverview', emptyKey: 'notOnCall', tab: 'onCall' },
];

const collectNamedOptions = (
  employees: Employee[],
  getId: (employee: Employee) => string | undefined,
  getName: (employee: Employee) => string | undefined,
) => {
  const values = new Map<string, string>();
  for (const employee of employees) {
    const id = getId(employee);
    const name = getName(employee);
    if (id && name) {
      values.set(id, name);
    }
  }

  return Array.from(values.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((left, right) => left.name.localeCompare(right.name));
};

export const RosterPage: React.FC = () => {
  const { t } = useTranslation(['rostering', 'common']);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const { canManageAll, canManageOwn, canViewEmployee } = useRosteringAccess();

  const viewMode = parseRosterViewMode(searchParams.get('view'));
  const weekStart = React.useMemo(
    () => startOfWeekMonday(parseWeekStartParam(searchParams.get('week')) ?? new Date()),
    [searchParams],
  );
  const monthStart = React.useMemo(
    () => parseMonthParam(searchParams.get('month'), weekStart),
    [searchParams, weekStart],
  );

  const [employees, setEmployees] = React.useState<Employee[]>([]);
  const [rules, setRules] = React.useState<RosterScheduleRule[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [departmentFilter, setDepartmentFilter] = React.useState('');
  const [locationFilter, setLocationFilter] = React.useState('');
  const [search, setSearch] = React.useState('');

  const periodDays = React.useMemo(
    () => (viewMode === 'month' ? getMonthDays(monthStart, t) : getWeekDays(weekStart, t)),
    [monthStart, t, viewMode, weekStart],
  );

  const todayKey = React.useMemo(() => toDateKey(new Date()), []);

  const changeViewMode = React.useCallback((nextView: RosterViewMode) => {
    navigate(buildRosterListUrl({
      view: nextView,
      weekStart,
      monthStart: nextView === 'month' ? monthStart : startOfMonth(weekStart),
    }));
  }, [monthStart, navigate, weekStart]);

  const changeWeek = React.useCallback((nextWeekStart: Date) => {
    navigate(buildRosterListUrl({
      view: 'week',
      weekStart: nextWeekStart,
      monthStart,
    }));
  }, [monthStart, navigate]);

  const changeMonth = React.useCallback((nextMonthStart: Date) => {
    navigate(buildRosterListUrl({
      view: 'month',
      weekStart,
      monthStart: nextMonthStart,
    }));
  }, [navigate, weekStart]);

  const departments = React.useMemo(
    () => collectNamedOptions(employees, (employee) => employee.departmentId, (employee) => employee.departmentName),
    [employees],
  );

  const locations = React.useMemo(
    () => collectNamedOptions(employees, (employee) => employee.locationId, (employee) => employee.locationName),
    [employees],
  );

  const filteredEmployees = React.useMemo(() => {
    const query = search.trim().toLowerCase();

    return employees.filter((employee) => {
      if (!canViewEmployee(employee.id)) {
        return false;
      }

      if (departmentFilter && employee.departmentId !== departmentFilter) {
        return false;
      }

      if (locationFilter && employee.locationId !== locationFilter) {
        return false;
      }

      if (!query) {
        return true;
      }

      return [employee.name, employee.email, employee.positionName, employee.departmentName, employee.locationName]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [canViewEmployee, departmentFilter, employees, locationFilter, search]);

  const employeeIds = React.useMemo(
    () => filteredEmployees.map((employee) => employee.id),
    [filteredEmployees],
  );

  const scheduleCellMap = React.useMemo(
    () => buildRosterCellMap(rules, periodDays, employeeIds, 'schedule'),
    [employeeIds, periodDays, rules],
  );

  const onCallCellMap = React.useMemo(
    () => buildRosterCellMap(rules, periodDays, employeeIds, 'onCall'),
    [employeeIds, periodDays, rules],
  );

  const cellMaps = React.useMemo<Record<OverviewKind, ReturnType<typeof buildRosterCellMap>>>(() => ({
    schedule: scheduleCellMap,
    onCall: onCallCellMap,
  }), [onCallCellMap, scheduleCellMap]);

  const loadRoster = React.useCallback(async () => {
    setLoading(true);

    try {
      const [nextEmployees, nextRules] = await Promise.all([
        listRosterEmployees(),
        listRosterRules(),
      ]);
      setEmployees(nextEmployees);
      setRules(nextRules);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  React.useEffect(() => {
    void loadRoster();
  }, [loadRoster]);

  const openEmployeeSchedule = (
    employeeId: string,
    tab: WeekScheduleTab,
    day?: RosterWeekDay,
  ) => {
    if (!canViewEmployee(employeeId)) {
      return;
    }

    const targetWeekStart = day ? startOfWeekMonday(day.date) : weekStart;
    const targetMonthStart = day ? startOfMonth(day.date) : monthStart;

    navigate(buildEmployeeScheduleUrl({
      employeeId,
      view: viewMode,
      weekStart: targetWeekStart,
      monthStart: targetMonthStart,
      tab,
    }));
  };

  const gridStyle = React.useMemo(
    () => ({
      gridTemplateColumns: `minmax(200px, 240px) repeat(${periodDays.length}, minmax(${viewMode === 'month' ? 72 : 128}px, 1fr))`,
      minWidth: viewMode === 'month' ? `${240 + periodDays.length * 72}px` : '1100px',
    }),
    [periodDays.length, viewMode],
  );

  const renderOverviewGrid = (overview: OverviewConfig) => {
    const cellMap = cellMaps[overview.kind];

    return (
      <section key={overview.kind} className={styles.overviewSection}>
        <h3 className={styles.overviewTitle}>{t(overview.titleKey)}</h3>

        <div className={styles.gridWrap}>
          <div
            className={`${styles.grid} ${viewMode === 'month' ? styles.gridMonth : ''}`}
            style={gridStyle}
          >
            <div className={`${styles.cell} ${styles.cornerCell}`}>{t('employee')}</div>

            {periodDays.map((day) => (
              <div
                key={`${overview.kind}-${day.dayKey}`}
                className={[
                  styles.cell,
                  styles.headerCell,
                  viewMode === 'month' ? styles.headerCellMonth : '',
                  day.dayKey === todayKey ? styles.todayHeaderCell : '',
                ].filter(Boolean).join(' ')}
                title={day.dayLabel}
              >
                <span className={styles.headerWeekday}>{day.weekdayLabel}</span>
                <span className={styles.headerDate}>
                  <span className={styles.headerDayNumber}>{day.dayNumberLabel}</span>
                  {viewMode === 'month' ? null : (
                    <span className={styles.headerMonth}>{day.monthLabel}</span>
                  )}
                </span>
              </div>
            ))}

            {filteredEmployees.map((employee) => (
              <React.Fragment key={`${overview.kind}-${employee.id}`}>
                <button
                  type="button"
                  className={`${styles.cell} ${styles.employeeCell} ${styles.employeeCellButton}`}
                  onClick={() => openEmployeeSchedule(employee.id, overview.tab)}
                >
                  <div className={styles.employeeName}>{employee.name}</div>
                  <div className={styles.employeeMeta}>
                    {[employee.positionName, employee.departmentName, employee.locationName].filter(Boolean).join(' · ') || employee.email}
                  </div>
                </button>

                {periodDays.map((day) => {
                  const entries = cellMap[`${employee.id}|${day.dayKey}`] || [];
                  const summary = entries.length === 0
                    ? t(overview.emptyKey)
                    : entries.map((entry) => entry.label).join(', ');

                  return (
                    <button
                      key={`${overview.kind}-${employee.id}-${day.dayKey}`}
                      type="button"
                      className={[
                        styles.cell,
                        styles.dayCell,
                        styles.dayCellButton,
                        viewMode === 'month' ? styles.dayCellMonth : '',
                        day.dayKey === todayKey ? styles.todayColumnCell : '',
                        entries.length === 0 ? styles.dayCellEmpty : '',
                      ].filter(Boolean).join(' ')}
                      aria-label={`${employee.name} · ${day.dayLabel} · ${summary}`}
                      onClick={() => openEmployeeSchedule(employee.id, overview.tab, day)}
                    >
                      <div className={styles.entryList}>
                        {entries.length === 0 ? (
                          /* Month columns are too narrow for the placeholder; the aria-label carries it. */
                          viewMode === 'month' ? null : (
                            <span className={styles.emptyDayLabel}>{t(overview.emptyKey)}</span>
                          )
                        ) : entries.map((entry) => (
                          <span
                            key={`${entry.ruleId}-${day.dayKey}`}
                            className={`${styles.entryChip} ${entry.type === 'staffTimeOff' ? styles.entryChipTimeOff : styles.entryChipShift} ${viewMode === 'month' ? styles.entryChipMonth : ''}`}
                          >
                            {entry.label}
                          </span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className={`${styles.page} contact-list`}>
      <div className={headerStyles.pageHeader}>
        <div className={`${headerStyles.topBar} ${headerStyles.topBarDivided}`}>
          <div className={headerStyles.topBarLeading}>
            <ViewModeToggle value={viewMode} onChange={changeViewMode} />
            <WeekRangePicker
              mode={viewMode}
              weekStart={weekStart}
              monthStart={monthStart}
              onWeekChange={changeWeek}
              onMonthChange={changeMonth}
              className={headerStyles.weekControls}
            />
          </div>

          <div className={headerStyles.topBarTrailing}>
            <div className={headerStyles.headerFilters}>
              <input
                className={`text-input ${headerStyles.searchInput}`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('searchEmployees')}
              />
              <select
                className={`text-input ${headerStyles.filterSelect}`}
                value={departmentFilter}
                onChange={(event) => setDepartmentFilter(event.target.value)}
                aria-label={t('allDepartments')}
              >
                <option value="">{t('allDepartments')}</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>{department.name}</option>
                ))}
              </select>
              <select
                className={`text-input ${headerStyles.filterSelect}`}
                value={locationFilter}
                onChange={(event) => setLocationFilter(event.target.value)}
                aria-label={t('allLocations')}
              >
                <option value="">{t('allLocations')}</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <Spinner text={t('loading')} />
        </div>
      ) : (
        <div className={styles.overviewStack}>
          {OVERVIEWS.map((overview) => renderOverviewGrid(overview))}
        </div>
      )}

      {!loading && (canManageAll || canManageOwn) ? (
        <p className={styles.gridHint}>
          {viewMode === 'month' ? t('openEmployeeMonthHint') : t('openEmployeeWeekHint')}
        </p>
      ) : null}
    </div>
  );
};
